import { useState } from "react";
import ResizableDrawer from "@/components/UI/ResizableDrawer";
import { useScopeParams } from "@/hooks/useScopeParams";
import NewsIcon from "@/assets/icons/newsmode.svg?react";
import MarkdownRenderer from "@/components/UI/MarkdownRenderer";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useUser } from "@/contexts/UserContext";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import { ROLE_OPTIONS } from "@/contexts/AssumedRoleContext";
import {
  useTrialNewsLatest,
  useTrialNewsRefresh,
} from "@/hooks/useTrialNewsQuery";
import TrialNewsReport, { type InsightReportPayload } from "./TrialNewsReport";
import TrialOverviewReport from "./TrialOverviewReport";
import { toBackendTrialId } from "@/utils/trialIdMap";
import trialNewsContent from "../sections/demo/demo_trialnews.md?raw";
import bbTrialNewsContent from "../sections/bb_trialnews.md?raw";

import { trials } from "@/utils/trials";

// The trial-wide executive briefing pseudo-role the drawer lands on; the six
// Vivo Agent role reports are reachable through the same selector.
const OVERVIEW_ROLE = "trial-overview";

const AGENT_OPTIONS = [
  { key: OVERVIEW_ROLE, label: "Trial Overview" },
  ...ROLE_OPTIONS,
];

// Trial News drawer. With the `trial-news` feature flag enabled, content is a
// role-specific insight report generated on demand by the backend's Role
// Insight Agents (latest report + Refresh that polls a new run to completion).
// Without the flag it falls back to the legacy hardcoded demo markdown.
// Trial metadata in the header is still hardcoded demo data.
export default function TrialNewsDrawer() {
  const { id } = useScopeParams();
  const backendTrialId = toBackendTrialId(id ?? "");
  const liveNewsEnabled = useFeatureFlag(FEATURE_FLAGS.TRIAL_NEWS);
  // Regeneration is separately gated so demo tenants can show pre-generated
  // baseline reports without risking an accidental overwrite of the display.
  const refreshEnabled = useFeatureFlag(FEATURE_FLAGS.TRIAL_NEWS_REFRESH);
  const { userInfo } = useUser();

  const [open, setOpen] = useState(false);
  const [role, setRole] = useState<string>(OVERVIEW_ROLE);

  // Every open lands back on the trial-wide overview.
  const openDrawer = () => {
    setRole(OVERVIEW_ROLE);
    setOpen(true);
  };

  const { data, isLoading, isError } = useTrialNewsLatest(
    backendTrialId,
    role,
    open && liveNewsEnabled,
  );
  const refresh = useTrialNewsRefresh(backendTrialId, role);

  const latest = data?.latest ?? null;
  const activeRun = data?.active_run ?? null;
  const generating = !!activeRun || refresh.isPending;
  const failedRun = latest === null && !generating && isError;
  const generatedAt = latest?.completed_at
    ? new Date(latest.completed_at).toLocaleString()
    : null;

  // Header metadata. The DARATU-ALZ route keeps its curated demo persona
  // (LUMINA AD); every other trial shows its real record from the user's
  // available trials — never invented values.
  // available_trials is loosely typed on UserContext (see useFeatureFlag).

  const thisTrial = (userInfo?.available_trials as any[] | undefined)?.find(
    (trial) => trial?.id === backendTrialId,
  ) as { id: string; name?: string; phase?: string | number } | undefined;

  // Same backend-id keyed check as the homepage: the LUMINA AD demo persona
  // applies on both the DARATU-ALZ and DEMO_TRIAL routes.
  const isLuminaDemo =
    id === trials.demo.DARATUMUMAB || backendTrialId === "DEMO_TRIAL";
  const trialData = isLuminaDemo
    ? {
        name: "LUMINA AD",
        phase: "2",
        therapeuticArea: "Neurology",
        asset: "Neurolexa",
        status: "Active" as TrialStatus,
      }
    : {
        name: thisTrial?.name || backendTrialId,
        phase: thisTrial?.phase != null ? String(thisTrial.phase) : null,
        therapeuticArea: null,
        asset: null,
        status: null,
      };
  const headersArray = [
    {
      key: "TRIAL",
      value: (
        <div className="flex items-center gap-2">
          <p>{trialData.name}</p>
          {trialData.status && <TrialStatusPill status={trialData.status} />}
          {trialData.phase && <PhasePill phase={trialData.phase} />}
        </div>
      ),
    },
    ...(trialData.asset ? [{ key: "ASSET", value: trialData.asset }] : []),
    ...(trialData.therapeuticArea
      ? [{ key: "THERAPEUTIC AREA", value: trialData.therapeuticArea }]
      : []),
  ];

  const legacyContent =
    id === trials.demo.DARATUMUMAB ? trialNewsContent : bbTrialNewsContent;

  return (
    <>
      <button
        className="bg-[#06709B] text-white flex justify-center items-center text-md font-bold border-none gap-2 px-2 py-2 rounded-md"
        onClick={openDrawer}
      >
        <NewsIcon />
        Trial News
      </button>
      <ResizableDrawer
        className="trial-news-drawer"
        headerTitle={
          <h1 className="text-white text-[16px] font-bold uppercase">
            Trial News
          </h1>
        }
        headersArray={headersArray}
        open={open}
        onClose={() => setOpen(false)}
        sizeToggle={{
          collapsedWidth: "75%",
          expandedWidth: "90%",
          initialWidth: "75%",
        }}
        closable={false}
        closeIcon={null}
      >
        {!liveNewsEnabled ? (
          <div className="bg-white p-4 rounded-md text-[#556C75] markdown">
            <MarkdownRenderer content={legacyContent} />
          </div>
        ) : (
          <div className="bg-white p-4 rounded-md text-[#556C75]">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-[#E5EAEC] pb-3">
              <div className="flex items-center gap-2">
                <label
                  htmlFor="trial-news-role"
                  className="text-[12px] font-bold uppercase text-[#556C75]"
                >
                  Vivo Agent
                </label>
                <select
                  id="trial-news-role"
                  className="rounded-md border border-[#C6D2D7] px-2 py-1 text-[13px]"
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                  disabled={generating}
                >
                  {AGENT_OPTIONS.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-3">
                {generatedAt && !generating && (
                  <span className="text-[12px] text-[#8AA0A9]">
                    Generated {generatedAt}
                  </span>
                )}
                {refreshEnabled && (
                  <button
                    className="rounded-md bg-[#06709B] px-3 py-1.5 text-[13px] font-bold text-white disabled:opacity-50"
                    onClick={() => refresh.mutate()}
                    disabled={generating || isLoading}
                  >
                    {generating ? "Generating…" : "Refresh"}
                  </button>
                )}
              </div>
            </div>

            {(isError || refresh.isError || activeRun?.status === "ERROR") && (
              <div className="mb-3 rounded-md bg-[#EBD1E0] px-3 py-2 text-[13px] text-[#73134A]">
                {failedRun
                  ? "Could not load trial news for this role."
                  : "Report generation hit a problem. Try Refresh again."}
              </div>
            )}

            {generating && (
              <div className="mb-3 rounded-md bg-[#D2EFF9] px-3 py-2 text-[13px] text-[#064C68]">
                Generating a fresh {roleLabel(role)} report — this can take a
                few minutes.
                {latest
                  ? " The latest completed report stays available below."
                  : ""}
              </div>
            )}

            {isLoading ? (
              <p className="text-[13px] text-[#8AA0A9]">Loading…</p>
            ) : latest?.summary_markdown ? (
              role === OVERVIEW_ROLE ? (
                <TrialOverviewReport
                  report={latest.report as InsightReportPayload | null}
                  fallbackMarkdown={latest.summary_markdown}
                />
              ) : (
                <TrialNewsReport
                  report={latest.report as InsightReportPayload | null}
                  fallbackMarkdown={latest.summary_markdown}
                  roleLabel={roleLabel(role)}
                  trialId={backendTrialId}
                />
              )
            ) : (
              !generating &&
              !isError && (
                <div className="py-8 text-center text-[13px] text-[#8AA0A9]">
                  <p>
                    No {roleLabel(role)} report has been generated for this
                    trial yet.
                  </p>
                  {refreshEnabled && (
                    <button
                      className="mt-3 rounded-md bg-[#06709B] px-3 py-1.5 font-bold text-white"
                      onClick={() => refresh.mutate()}
                    >
                      Generate report
                    </button>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </ResizableDrawer>
    </>
  );
}

function roleLabel(roleKey: string): string {
  return (
    AGENT_OPTIONS.find((option) => option.key === roleKey)?.label ?? roleKey
  );
}

type TrialStatus = "Active" | "Complete" | "Caution" | "At Risk";

const getStatusColor = (status: string) => {
  const colorMap: { [key: string]: { bg: string; text: string } } = {
    Active: { bg: "#BDE0CC", text: "#004A42" },
    Complete: { bg: "#E0F2FE", text: "#0284C7" },
    Caution: { bg: "#FEF3C7", text: "#D97706" },
    "At Risk": { bg: "#FEE2E2", text: "#DC2626" },
  };
  return colorMap[status] || colorMap.Active;
};

const TrialStatusPill = ({ status }: { status: TrialStatus }) => {
  return (
    <span
      className="rounded-full px-1.5 py-0.5 text-[12px]"
      style={{
        backgroundColor: getStatusColor(status).bg,
        color: getStatusColor(status).text,
      }}
    >
      {status}
    </span>
  );
};

const PhasePill = ({ phase }: { phase: string }) => {
  return (
    <span className="rounded-md bg-[#556C75] px-1.5 py-0.5 text-[12px] uppercase text-white">
      Phase {phase}
    </span>
  );
};
